import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';

const CHALLENGES = ['Weak passwords', 'No backups', 'Suspect devices', 'Lack of policy', 'Unpatched apps', 'Other'];
const HELP = ['Penetration testing', 'SOC/monitoring', 'Training', 'Cloud security', 'Data protection', 'Product solutions'];
const TECH = ['AWS','Docker','React','Python','SIEM','Firewall','MFA','Mobile App','IoT','VPN','IAM','Email security','Endpoint','Backups','Forensics','Other'];

export default function EstimateForm({ onClose }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    challenges: [],
    help: [],
    tech: [],
    budget: '',
    description: '',
    contact: { email: '', phone: '', company: '', role: '' },
    nda: false
  });

  const toggle = (key, val) => {
    const arr = data[key];
    const exists = arr.includes(val);
    const next = exists ? arr.filter(a => a !== val) : [...arr, val];
    setData({ ...data, [key]: next });
  };

  const canNext = () => {
    if (step === 1) return data.challenges.length > 0;
    if (step === 2) return data.help.length > 0;
    if (step === 3) return data.tech.length > 0;
    return true;
  };

  const submit = () => {
    const templateParams = {
      challenges: data.challenges.join(', '),
      help: data.help.join(', '),
      tech: data.tech.join(', '),
      budget: data.budget,
      description: data.description,
      contact_email: data.contact.email,
      contact_phone: data.contact.phone,
      contact_company: data.contact.company,
      contact_role: data.contact.role,
      nda: data.nda ? 'Yes' : 'No',
      time: new Date().toLocaleString()
    };

    emailjs
      .send('service_19akvu7', 'template_n2yv0tf', templateParams, 'slbL3jadh8Ujmt1Ic')
      .then(() => {
        toast.dismiss();
        toast.success('✅ Your estimate request has been sent!', {
          position: 'top-right',
          autoClose: 3000,
          pauseOnHover: true
        });
        setTimeout(() => {
          toast.dismiss();
          onClose();
        }, 2000);
      })
      .catch(err => {
        console.error(err);
        toast.dismiss();
        toast.error('❌ Failed to send. Please try again.', {
          position: 'top-right',
          autoClose: 4000
        });
      });
  };

  const buttonBase = {
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  };

  const backButtonStyle = {
    ...buttonBase,
    background: '#f5f5f5',
    color: '#333',
    border: '1px solid #e6eef2'
  };

  const continueButtonStyle = (enabled) => ({
    ...buttonBase,
    background: enabled ? '#0a0f29' : '#e6eef2',
    color: enabled ? '#fff' : '#aaa'
  });

  const requestButtonStyle = {
    ...buttonBase,
    background: '#ff6600',
    color: '#fff'
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 999,
        background: 'rgba(10,15,41,0.8)',
        overflowY: 'auto',
        padding: '20px'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 880,
          background: '#fff',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{ padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eef2f5' }}>
          <strong>Request Estimate</strong>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Close</button>
        </div>

        {/* Body */}
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ marginBottom: 12, color: '#6b7280' }}>Step {step} of 5</div>
          <div style={{ height: 8, background: '#eef2f5', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${(step/5)*100}%`, height: 8, background: 'linear-gradient(90deg,#0a0f29,#00ff88)'}}></div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxHeight: '60vh', overflowY: 'auto' }}>
            {step===1 && (
              <div>
                <h4>What are the challenges?</h4>
                <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                  {CHALLENGES.map(c => (
                    <label key={c} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '14px' }}>
                      <input type='checkbox' checked={data.challenges.includes(c)} onChange={() => toggle('challenges', c)} /> {c}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {step===2 && (
              <div>
                <h4>What can we help with?</h4>
                <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                  {HELP.map(h => (
                    <label key={h} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '14px' }}>
                      <input type='checkbox' checked={data.help.includes(h)} onChange={() => toggle('help', h)} /> {h}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {step===3 && (
              <div>
                <h4>Specific technologies?</h4>
                <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                  {TECH.map(t => (
                    <label key={t} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '14px' }}>
                      <input type='checkbox' checked={data.tech.includes(t)} onChange={() => toggle('tech', t)} /> {t}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {step===4 && (
              <div>
                <h4>Project details</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                  <input placeholder='Budget' value={data.budget} onChange={e => setData({...data, budget: e.target.value})} style={{ padding: 12, borderRadius: 8, border: '1px solid #eef2f5' }} />
                  <textarea placeholder='Description' value={data.description} onChange={e => setData({...data, description: e.target.value})} style={{ padding: 12, borderRadius: 8, border: '1px solid #eef2f5' }} rows={4} />
                  <div style={{ color:'#6b7280', fontSize: '13px' }}>Hint: project details, goals, design ready, new/ongoing, deadline</div>
                </div>
              </div>
            )}
            {step===5 && (
              <div>
                <h4>Your details</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                  <input placeholder='Email' value={data.contact.email} onChange={e => setData({...data, contact:{...data.contact,email:e.target.value}})} style={{ padding: 12, borderRadius: 8, border:'1px solid #eef2f5' }} />
                  <input placeholder='Phone' value={data.contact.phone} onChange={e => setData({...data, contact:{...data.contact,phone:e.target.value}})} style={{ padding: 12, borderRadius: 8, border:'1px solid #eef2f5' }} />
                  <input placeholder='Company' value={data.contact.company} onChange={e => setData({...data, contact:{...data.contact,company:e.target.value}})} style={{ padding: 12, borderRadius: 8, border:'1px solid #eef2f5' }} />
                  <input placeholder='Role' value={data.contact.role} onChange={e => setData({...data, contact:{...data.contact,role:e.target.value}})} style={{ padding: 12, borderRadius: 8, border:'1px solid #eef2f5' }} />
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '14px' }}>
                    <input type='checkbox' checked={data.nda} onChange={e => setData({...data, nda:e.target.checked})} /> Do you need an NDA first?
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            {step > 1 && (
              <button onClick={() => setStep(s => s-1)} style={backButtonStyle}>Back</button>
            )}
            {step < 5 && (
              <button onClick={() => canNext() && setStep(s => s+1)} disabled={!canNext()} style={continueButtonStyle(canNext())}>Continue</button>
            )}
            {step === 5 && (
              <button onClick={submit} style={requestButtonStyle}>Request Estimate</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
